import axios, { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { APIEndpointEnum } from "../../../enums/endpoint";
import { API_URL } from "../../../utils/env";
import { useInviteStatus } from "./useInviteStatus";

export interface ClaimInviteResult {
  success?: boolean;
  status?: string;
  message?: string;
  account?: string;
  redeemed_code?: string;
  [key: string]: unknown;
}

interface UseClaimInviteState {
  isClaiming: boolean;
  error: string | null;
  data: ClaimInviteResult | null;
}

const INITIAL_STATE: UseClaimInviteState = {
  isClaiming: false,
  error: null,
  data: null,
};

const toErrorMessage = (value: unknown, fallback: string): string => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.message === "string") return record.message;
    if (typeof record.error === "string") return record.error;
    if (record.error) return toErrorMessage(record.error, fallback);
  }
  return fallback;
};

const normalizeInviteErrorMessage = (
  value: unknown,
  status?: number,
  code?: unknown
): string => {
  const message = toErrorMessage(value, "Invite claim failed.");
  const normalizedCode = typeof code === "string" ? code.toUpperCase() : "";
  const normalizedMessage = message.toLowerCase();

  if (
    status === 409 ||
    normalizedCode.includes("ALREADY") ||
    normalizedCode.includes("CONFLICT") ||
    normalizedMessage.includes("already") ||
    normalizedMessage.includes("used")
  ) {
    return "This invite code has already been used. Use a new code or connect the wallet that claimed it.";
  }

  if (
    normalizedCode === "OPEN_INVITE_UNAVAILABLE" ||
    normalizedMessage.includes("service could not verify")
  ) {
    return "That invite code could not be verified. Check the code and try again.";
  }

  if (
    status === 400 ||
    status === 403 ||
    status === 404 ||
    status === 422 ||
    normalizedMessage.includes("invalid")
  ) {
    return "Enter a valid invite code to unlock access.";
  }

  return message;
};

/**
 * Claims an Open Invite code for the connected wallet via the backend proxy.
 *
 * The backend route (POST `/invite/claim`) forwards to the upstream Open
 * Invite service; the API key never leaves the server.
 */
export const useClaimInvite = () => {
  const [state, setState] = useState<UseClaimInviteState>(INITIAL_STATE);
  const { mutate: mutateInviteStatus } = useInviteStatus();

  const claim = useCallback(
    async (code: string, walletAddress: string): Promise<ClaimInviteResult> => {
      const normalizedCode = code.trim();
      const normalizedAddress = walletAddress.trim();

      if (!normalizedCode) {
        const message = "Invite code is required.";
        setState({ isClaiming: false, error: message, data: null });
        throw new Error(message);
      }
      if (!normalizedAddress) {
        const message = "Wallet address is required.";
        setState({ isClaiming: false, error: message, data: null });
        throw new Error(message);
      }

      setState({ isClaiming: true, error: null, data: null });
      try {
        const response = await axios.post<ClaimInviteResult>(
          `${API_URL}${APIEndpointEnum.INVITE_CLAIM}`,
          { code: normalizedCode, walletAddress: normalizedAddress },
          { headers: { "content-type": "application/json" } }
        );
        setState({ isClaiming: false, error: null, data: response.data });
        mutateInviteStatus();
        return response.data;
      } catch (err) {
        const axiosErr = err as AxiosError<{
          code?: unknown;
          error?: unknown;
          message?: unknown;
        }>;
        const payload = axiosErr.response?.data;
        const rawMessage =
          axiosErr.response?.data?.error ??
            axiosErr.response?.data?.message ??
            axiosErr.response?.data ??
            axiosErr.message;
        const message = normalizeInviteErrorMessage(
          rawMessage,
          axiosErr.response?.status,
          payload?.code
        );
        setState({ isClaiming: false, error: message, data: null });
        throw new Error(message);
      }
    },
    [mutateInviteStatus]
  );

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  return { claim, reset, ...state };
};
